/**
 * /auth/recovery — Account recovery tiers (§14 of UX spec).
 *
 * Tier 1 — I have my email + MFA device (self-serve, automatic).
 * Tier 2 — I have my email but not my MFA device. Two sub-paths (lane C ·
 *          C5/C6, 2026-07-09):
 *            a) SELF-SERVE with a single-use backup code — the inline flow on
 *               this page: verify the code (POST /v1/auth/backup-codes/verify)
 *               then reset MFA (POST /v1/auth/recovery/mfa-reset), then hand
 *               off to sign-in where re-enrolment continues.
 *            b) Ask the tenant admin (queue → approval) — /auth/recovery/lost-both.
 * Tier 3 — I lost everything (catastrophic — Tier 3 superadmin queue, days not
 *          minutes).
 *
 * SECURITY — the backup-code flow mirrors the handlers' existence-leak
 * contract EXACTLY: every verify failure renders ONE generic message
 * (unknown email / malformed / wrong / already-used are indistinguishable);
 * the per-IP rate limit (5/min, the attempts-exhausted state) renders as its
 * own explicit state; a reset 502 says honestly that the code was consumed.
 * Never add copy that distinguishes "no such account" from "wrong code".
 */

import { useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AuthLayout } from '../../components/layouts/auth-layout';
import { AuthSubmit } from '../../components/auth/auth-controls';
import { BackToSignIn } from '../../components/auth/back-to-sign-in';
import { useVerifyBackupCode, useMfaReset, ApiError } from '../../lib/api';

export const Route = createFileRoute('/auth/recovery')({
  component: RecoveryPage,
});

type Tier = 1 | 2 | 3 | null;
type Tier2Method = 'backup' | 'admin';
type View = 'tiers' | 'backup' | 'done';

const TIERS: Array<{
  id: Tier;
  label: string;
  time: string;
  tone: string;
  body: string;
  next: string;
  borderColor: string;
  badgeBg: string;
  badgeFg: string;
}> = [
  {
    id: 1,
    label: 'I have my email and my authenticator app',
    time: 'Automatic',
    tone: 'positive',
    body: 'Self-service recovery. We verify your identity via email and re-enrol your MFA device in a single step.',
    next: 'You will receive a recovery link at your registered email address. Follow the instructions to re-enrol your authenticator.',
    borderColor: 'var(--hairline)',
    badgeBg: 'var(--positive-soft)',
    badgeFg: 'var(--positive)',
  },
  {
    id: 2,
    label: 'I have my email but I no longer have my MFA device',
    time: 'Instant with a code',
    tone: 'warn',
    body: 'If you saved single-use backup codes when you set up MFA, you can reset your authenticator yourself right now. Otherwise your firm administrator approves the reset.',
    next: '',
    borderColor: 'var(--accent-300)',
    badgeBg: 'var(--accent-100)',
    badgeFg: 'var(--accent-700)',
  },
  {
    id: 3,
    label: 'I have lost access to both my email and my MFA device',
    time: '1-3 business days',
    tone: 'negative',
    body: 'This is treated as a potential security incident. Senior Elreign staff must verify your identity out of band before access is restored. The process is deliberate and takes time.',
    next: 'A support case is opened. You will need to verify your identity out of band with Elreign support. No access is granted until the review is complete.',
    borderColor: 'var(--negative-soft)',
    badgeBg: 'var(--negative-soft)',
    badgeFg: 'var(--negative)',
  },
];

/** The tier-2 self-serve vs admin-approval sub-choice. */
const TIER2_METHODS: Array<{ id: Tier2Method; label: string; detail: string }> = [
  {
    id: 'backup',
    label: 'I have a backup code',
    detail:
      'Instant. Enter one of the single-use backup codes you saved when setting up MFA: your authenticator is reset immediately and you re-enrol a new one after signing in.',
  },
  {
    id: 'admin',
    label: 'I don’t have my backup codes',
    detail:
      '1-2 business hours. We send a recovery request to your firm administrator. They contact you to verify your identity, then approve access on your behalf.',
  },
];

/** How the backup-code flow renders a failure. `kind` drives the visual state:
 *  'rate-limited' is the explicit attempts-exhausted notice; everything else
 *  is either the single generic invalid message (existence-leak contract) or
 *  an honest operational notice. */
interface RecoveryErrorState {
  kind: 'invalid' | 'rate-limited' | 'code-spent' | 'reset-incomplete' | 'unavailable' | 'not-eligible';
  message: string;
}

/** Map a failed verify/reset call onto the handlers' EXACT error contract.
 *  Deliberately collapses every invalid-input cause to one generic sentence —
 *  do not "improve" these messages with more specific causes. */
function mapRecoveryError(err: unknown): RecoveryErrorState {
  if (err instanceof ApiError) {
    const bodyMessage =
      err.body && typeof err.body === 'object' && typeof (err.body as Record<string, unknown>)['message'] === 'string'
        ? ((err.body as Record<string, unknown>)['message'] as string)
        : null;
    if (err.status === 429) {
      return {
        kind: 'rate-limited',
        message: bodyMessage ?? 'Too many attempts from this address. Please retry shortly.',
      };
    }
    if (err.status === 403 && err.code === 'BACKUP_CODE_NOT_ELIGIBLE') {
      // QA P0-1 (2026-10-04). The code is right, but it was made before
      // recovery codes needed a security check, so a password alone could have
      // made it. Nothing was changed and the code was NOT spent. The backend's
      // sentence says exactly that and where to go instead; render it as
      // written. Reachable only with a correct code, so naming it here does
      // not weaken the existence-leak rule above.
      return {
        kind: 'not-eligible',
        message:
          bodyMessage ??
          'This backup code cannot reset your authenticator on its own. Nothing on your account has changed. Ask your administrator for a reset instead, or contact support@elreign.io.',
      };
    }
    if (err.status === 400 || err.status === 401) {
      // verify → 400 INVALID_CODE · reset → 401 INVALID_RECOVERY_TOKEN. One
      // generic rendering for both (anti-enumeration).
      return {
        kind: 'invalid',
        message: 'The email or code provided is invalid.',
      };
    }
    if (err.status === 502 && err.code === 'MFA_RESET_INCOMPLETE') {
      // A HALF-DONE WIPE IS NOT A FAILED ONE. Some factors were destroyed and
      // at least one survived, so "try a different backup code" is the wrong
      // instruction twice over: it spends a second code on a reset that cannot
      // finish, and it says nothing about the factor still live on the account.
      // The backend's own sentence is the one that has the facts, so render it.
      return {
        kind: 'reset-incomplete',
        message:
          bodyMessage ??
          // Deliberately says nothing about what survived. The backend writes
          // that sentence from the factors it listed against the ones it
          // actually deleted, and there are two answers: a factor still in
          // place, or none at all. A fallback that picks one would be wrong
          // half the time, and the half it gets wrong tells somebody with no
          // second factor left that they still have one.
          'Your authentication factors were changed, but the reset did not finish. Do not sign in again until you have contacted support@elreign.io. The backup code used on this attempt has been spent.',
      };
    }
    if (err.status === 502) {
      // BAD_GATEWAY_SUPABASE: the code was consumed before the downstream reset
      // failed and nothing was destroyed, so the honest recovery is a DIFFERENT
      // code (mirrors the handler's own message).
      return {
        kind: 'code-spent',
        message:
          'That backup code has been used, but the reset could not be completed. Please try again with a different backup code, or contact support.',
      };
    }
    if (err.status === 503) {
      return {
        kind: 'unavailable',
        message:
          'Account recovery is temporarily unavailable. Please try again shortly, or contact support@elreign.io.',
      };
    }
  }
  return {
    kind: 'invalid',
    message: 'Something went wrong. Please check your connection and try again.',
  };
}

/** Seconds the resubmit stays disabled after a 429 — matches the handlers'
 *  per-minute damper window so the retry actually has a chance. */
const RATE_LIMIT_COOLDOWN_MS = 60_000;

function RecoveryPage() {
  const [view, setView] = useState<View>('tiers');
  const [selected, setSelected] = useState<Tier>(null);
  const [tier2Method, setTier2Method] = useState<Tier2Method>('backup');
  const selectedTier = TIERS.find((t) => t.id === selected);
  const navigate = useNavigate();

  // ── Backup-code flow state (C5 verify → C6 reset, one submit) ──────────────
  const verify = useVerifyBackupCode();
  const reset = useMfaReset();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<RecoveryErrorState | null>(null);
  const [coolingDown, setCoolingDown] = useState(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const busy = verify.isPending || reset.isPending;

  function handleBegin() {
    if (selected === 1) {
      void navigate({ to: '/auth/forgot' });
    } else if (selected === 2) {
      if (tier2Method === 'backup') {
        setError(null);
        setView('backup');
      } else {
        void navigate({ to: '/auth/recovery/lost-both' });
      }
    } else if (selected === 3) {
      void navigate({ to: '/auth/recovery/lost-both' });
    }
  }

  function startCooldown() {
    setCoolingDown(true);
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    cooldownTimer.current = setTimeout(() => setCoolingDown(false), RATE_LIMIT_COOLDOWN_MS);
  }

  async function submitBackupCode(e: React.FormEvent) {
    e.preventDefault();
    if (busy || coolingDown) return;
    setError(null);
    try {
      // C5 — consume the backup code, mint the signed single-use token…
      const verified = await verify.mutateAsync({ email: email.trim(), code: code.trim() });
      // C6 — …and redeem it immediately: flag the account's MFA factors for
      // re-enrolment. The pair is one user gesture; there is no meaningful
      // intermediate state to park the user in.
      await reset.mutateAsync({ email: email.trim(), recoveryToken: verified.recoveryToken });
      setView('done');
    } catch (err) {
      const mapped = mapRecoveryError(err);
      setError(mapped);
      if (mapped.kind === 'rate-limited') startCooldown();
    }
  }

  // ── Done — MFA reset recorded; hand off to sign-in for re-enrolment ────────
  if (view === 'done') {
    return (
      <AuthLayout
        eyebrow="Account recovery"
        heading="Authenticator reset."
        sub="Your backup code was accepted and your authenticator has been reset."
        stepUp
      >
        <div
          data-testid="recovery-done"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline-2)',
            borderRadius: 12,
            padding: '18px 20px',
            marginBottom: 20,
            fontSize: 13.5,
            color: 'var(--ink-2)',
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>What happens next</div>
          <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <li>Sign in with your email address and password.</li>
            <li>You&rsquo;ll be guided to set up a new authenticator app.</li>
            <li>Generate fresh backup codes from Security once you&rsquo;re in: the code you just used is now spent.</li>
          </ol>
          <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--ink-3)' }}>
            A confirmation email is on its way to you. If you did not request this
            reset, contact support@elreign.io immediately: someone may have one
            of your backup codes.
          </div>
        </div>
        <AuthSubmit
          type="button"
          onClick={() => void navigate({ to: '/auth/login' })}
          label="Continue to sign in"
          busyLabel="Continue to sign in"
          busy={false}
        />
      </AuthLayout>
    );
  }

  // ── Backup-code entry (C5/C6) ───────────────────────────────────────────────
  if (view === 'backup') {
    return (
      <AuthLayout
        eyebrow="Account recovery"
        heading="Use a backup code."
        sub="Enter your email address and one of the single-use backup codes you saved when you set up MFA."
        stepUp
      >
        <form onSubmit={(e) => void submitBackupCode(e)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <label htmlFor="recovery-email" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>Email address</span>
            <input
              id="recovery-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.co.za"
              autoComplete="email"
              required
              disabled={busy}
              aria-invalid={error !== null}
              aria-describedby={error !== null ? 'recovery-error' : undefined}
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 9,
                border: '1px solid var(--hairline-2)', background: 'var(--bg)',
                color: 'var(--ink)', fontFamily: 'var(--sans)', fontSize: 15,
                boxSizing: 'border-box',
              }}
            />
          </label>

          <label htmlFor="recovery-code" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>
              Backup code
              <span style={{ fontWeight: 400, color: 'var(--ink-4)' }}> · single-use</span>
            </span>
            <input
              id="recovery-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. a1b2-c3d4-e5f6"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              required
              minLength={8}
              maxLength={64}
              disabled={busy}
              aria-invalid={error !== null}
              aria-describedby={error !== null ? 'recovery-error' : undefined}
              className="mono"
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 9,
                border: '1px solid var(--hairline-2)', background: 'var(--bg)',
                color: 'var(--ink)', fontSize: 15, letterSpacing: '0.04em',
                boxSizing: 'border-box',
              }}
            />
          </label>

          {error !== null && (
            <div
              id="recovery-error"
              data-testid={`recovery-error-${error.kind}`}
              role="alert"
              aria-live="polite"
              style={
                error.kind === 'rate-limited' || error.kind === 'unavailable'
                  ? {
                      // Explicit attempts-exhausted / service state — amber, not red:
                      // the input wasn't judged, the gate is closed.
                      padding: '10px 12px', borderRadius: 8,
                      background: 'var(--accent-100)', border: '1px solid var(--accent-300)',
                      color: 'var(--accent-700)', fontSize: 13, lineHeight: 1.45,
                    }
                  : {
                      padding: '10px 12px', borderRadius: 8,
                      background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.3)',
                      color: '#991B1B', fontSize: 13, lineHeight: 1.45,
                    }
              }
            >
              {error.message}
              {error.kind === 'rate-limited' && (
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  You can try again in about a minute.
                </div>
              )}
              {error.kind === 'not-eligible' && (
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    data-testid="recovery-ask-admin"
                    onClick={() => void navigate({ to: '/auth/recovery/lost-both' })}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: 'inherit', fontWeight: 600, fontSize: 13, fontFamily: 'var(--sans)',
                      textDecoration: 'underline',
                    }}
                  >
                    Ask my administrator for a reset
                  </button>
                </div>
              )}
            </div>
          )}

          <AuthSubmit
            label="Reset my authenticator"
            busyLabel={verify.isPending ? 'Checking code…' : 'Resetting…'}
            busy={busy}
            disabled={coolingDown}
          />
        </form>

        <div
          style={{
            marginTop: 24, paddingTop: 18, borderTop: '1px solid var(--hairline)',
            fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.6,
          }}
        >
          <button
            type="button"
            onClick={() => setView('tiers')}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--primary-600)', fontWeight: 500, fontSize: 13.5, fontFamily: 'var(--sans)',
            }}
          >
            ← Other recovery options
          </button>
          {' · '}
          Remembered your device?{' '}
          <Link to="/auth/login" style={{ color: 'var(--primary-600)', fontWeight: 500 }}>
            Back to sign in
          </Link>
          .
        </div>
      </AuthLayout>
    );
  }

  // ── Tier selection ──────────────────────────────────────────────────────────
  return (
    <AuthLayout
      eyebrow="Account recovery"
      heading="Recover your account."
      sub="Select the option that best describes your situation."
    >
      {/* Tier cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {TIERS.map((tier) => {
          const isSelected = selected === tier.id;
          return (
            <button
              key={tier.id}
              type="button"
              onClick={() => setSelected(tier.id)}
              style={{
                width: '100%',
                textAlign: 'left',
                background: isSelected ? 'var(--surface-2)' : 'var(--bg)',
                border: `1px solid ${isSelected ? tier.borderColor : 'var(--hairline)'}`,
                borderRadius: 12,
                padding: '16px 18px',
                cursor: 'pointer',
                transition: 'all 160ms var(--ease-cushion)',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                fontFamily: 'var(--sans)',
              }}
            >
              {/* Radio dot */}
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: `2px solid ${isSelected ? 'var(--primary-700)' : 'var(--ink-4)'}`,
                  background: isSelected ? 'var(--primary-700)' : 'transparent',
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {isSelected && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: 'var(--surface)',
                    }}
                  />
                )}
              </span>

              <span style={{ flex: 1 }}>
                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', display: 'block', lineHeight: 1.4 }}>
                  {tier.label}
                </span>
              </span>

              {/* Time badge */}
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: tier.badgeBg,
                  color: tier.badgeFg,
                  letterSpacing: '0.04em',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {tier.time}
              </span>
            </button>
          );
        })}
      </div>

      {/* Expanded detail when a tier is selected */}
      {selectedTier && (
        <div
          style={{
            marginTop: 16,
            background: 'var(--surface)',
            border: `1px solid ${selectedTier.borderColor}`,
            borderRadius: 12,
            padding: '18px 20px',
            fontSize: 14,
            lineHeight: 1.6,
            color: 'var(--ink-2)',
          }}
        >
          <p style={{ margin: selected === 2 ? '0 0 12px' : '0 0 10px' }}>{selectedTier.body}</p>
          {selected === 2 ? (
            /* C5/C6 — the self-serve backup-code path vs the admin queue. */
            <div role="radiogroup" aria-label="How do you want to recover?" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {TIER2_METHODS.map((m) => {
                const isPicked = tier2Method === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="radio"
                    aria-checked={isPicked}
                    data-testid={`tier2-method-${m.id}`}
                    onClick={() => setTier2Method(m.id)}
                    style={{
                      textAlign: 'left',
                      background: isPicked ? 'var(--surface-2)' : 'var(--bg)',
                      border: `1px solid ${isPicked ? 'var(--primary-700)' : 'var(--hairline)'}`,
                      borderRadius: 10,
                      padding: '12px 14px',
                      cursor: 'pointer',
                      fontFamily: 'var(--sans)',
                    }}
                  >
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                      {m.label}
                    </span>
                    <span style={{ display: 'block', fontSize: 12.5, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.5 }}>
                      {m.detail}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, color: 'var(--ink)', fontWeight: 500 }}>
              {selectedTier.next}
            </p>
          )}
        </div>
      )}

      {/* CTA */}
      {selected !== null && (
        <div style={{ marginTop: 20 }}>
          <AuthSubmit type="button" onClick={handleBegin} label="Begin recovery" busyLabel="Begin recovery" busy={false} />
        </div>
      )}

      {/* DESIGN GATE B2 (grader round 11, desk pass 5). This is the screen
          people land on, and until now its only link was the logo, which goes
          to the marketing site. The backup-code step below already had this
          way back; the picker in front of it did not. Same lead-in as that
          step, because it is the same route and the same reader. */}
      <BackToSignIn prompt="Remembered your device?" />
    </AuthLayout>
  );
}
